import { createAction } from '@reduxjs/toolkit';
import { Point, Rect } from 'dragonmouse-shared/geometry';
import { AuthUser, DomElement, Quality } from 'dragonmouse-shared/interfaces';
import {
	DeviceConfig,
	GoldTooltipState,
	SilverTooltipState,
	LearningCenterState,
	Settings,
} from 'redux/mode/reducers';
import { TransferBoard } from 'transfer/TransferBoard';
import { Pick } from './classes';
import { Command } from './commands';
import {
	AlertModalOptions,
	ConfirmModalOptions,
	ContextMenu,
	DarkModeBehavior,
	LeftPanelTab,
	MouseDragMode,
	PendingChatInput,
	PromptModalOptions,
	GoldRefillModalOptions,
	SceneTransform,
	ToolType,
	ZoomType,
} from './reducers';

export const setActiveTool = createAction<ToolType>('SET_ACTIVE_TOOL');
export const setToolModified = createAction<boolean>('SET_TOOL_MODIFIED');
export const activateLastTool = createAction('ACTIVATE_LAST_TOOL');
export const setScale = createAction<number>('SET_SCALE');
export const setDarkModeBehavior = createAction<DarkModeBehavior>('SET_DARK_MODE_BEHAVIOR');
export const setSystemDarkMode = createAction<boolean>('SET_SYSTEM_DARK_MODE');
export const setOffset = createAction<Point>('SET_OFFSET');
export const resetOffset = createAction('RESET_OFFSET');
export const centerCanvasAroundScenePosition = createAction<Point>(
	'CENTER_CANVAS_AROUND_SCENE_POSITION'
);
// transforms the canvas as little as possible to show the provided bounding box
// if the bounding box is already fully visible, this will do nothing
export const transformCanvasToShowSceneBoundingBox = createAction<Rect>(
	'TRANSFORM_CANVAS_TO_SHOW_BOUNDING_BOX'
);
// transforms the canvas to neatly center the provided bounding box in view
export const centerCanvasAroundSceneBoundingBox = createAction<{
	boundingBox: Rect;
	animated: boolean;
	tightFit?: boolean;
	frameId?: string; // if frameId is provided, the specified frame will be focused
}>('CENTER_CANVAS_AROUND_BOUNDING_BOX');
export const setCanvasMousePosition = createAction<Point>('SET_CANVAS_MOUSE_POSITION');
export const setCanvasWidth = createAction<number>('SET_CANVAS_WIDTH');
export const setCanvasHeight = createAction<number>('SET_CANVAS_HEIGHT');
export const zoomSetAroundCanvasCenter = createAction<number>('ZOOM_SET_AROUND_CANVAS_CENTER');
// value is a float, representing the amount to multiple the scale factor by
// value = 2.0 means we will zoom in by 2x
export const zoomAroundCanvasCenter = createAction<number>('ZOOM_AROUND_CANVAS_CENTER');
export const zoomAroundMousePosition = createAction<ZoomType>('ZOOM_AROUND_MOUSE_POSITION');
export const setScaleAroundMousePosition = createAction<number>('SET_SCALE_AROUND_MOUSE_POSITION');
export const setMouseDragMode = createAction<MouseDragMode | null>('SET_MOUSE_DRAG_MODE');
export const setLoading = createAction<boolean>('SET_LOADING');
export const setExternalLocation = createAction<boolean>('SET_EXTERNAL_LOCATION');
export const setPanelsVisible = createAction<boolean>('SET_PANELS_VISIBLE');
export const setSidebarVisible = createAction<boolean>('SET_SIDEBAR_VISIBLE');
export const setLeftPanelWidth = createAction<{ tab: LeftPanelTab; width: number }>(
	'SET_LEFT_PANEL_WIDTH'
);
export const setRightPanelWidth = createAction<number>('SET_RIGHT_PANEL_WIDTH');
export const setInspectorRightPanelWidth = createAction<number>('SET_INSPECTOR_RIGHT_PANEL_WIDTH');
export const setInspectorLeftPanelWidth = createAction<number>('SET_INSPECTOR_LEFT_PANEL_WIDTH');
export const setInspectorDeepThinking = createAction<boolean>('SET_INSPECTOR_DEEP_THINKING');
export const setSelectedDomElements = createAction<DomElement[]>('SET_SELECTED_DOM_ELEMENTS');
export const showContextMenu = createAction<ContextMenu | null>('SHOW_CONTEXT_MENU');
export const showPromptModal = createAction<PromptModalOptions | null>('SHOW_PROMPT_MODAL');
export const showConfirmModal = createAction<ConfirmModalOptions | null>('SHOW_CONFIRM_MODAL');
export const showGoldRefillModal = createAction<GoldRefillModalOptions | null>(
	'SHOW_GOLD_REFILL_MODAL'
);
export const showAlertModal = createAction<AlertModalOptions | null>('SHOW_ALERT_MODAL');
export const setGuestSignupModalVisible = createAction<boolean>('SET_GUEST_SIGNUP_MODAL_VISIBLE');
// hovering and selection
// pass null into these to clear selection/hover
export const setSelected = createAction<Pick | Pick[] | null>('SET_SELECTED');
export const addToSelected = createAction<Pick>('ADD_TO_SELECTED');
export const removeFromSelected = createAction<Pick>('REMOVE_FROM_SELECTED');
export const setHovered = createAction<Pick | null>('SET_HOVERED');
export const setClipboard = createAction<TransferBoard | null>('SET_CLIPBOARD');
export const setUpdateNavigatorScrollPosition = createAction<boolean>(
	'SET_UPDATE_NAVIGATOR_SCROLL_POSITION'
);
export const setSettingsModalVisible = createAction<boolean>('SET_SETTINGS_MODAL_VISIBLE');
export const setActiveSceneId = createAction<string | null>('SET_ACTIVE_SCENE_ID');
export const setActiveChatId = createAction<string | null>('SET_ACTIVE_CHAT_ID');
export const setPendingChatInput = createAction<{
	chatId: string;
	pendingChatInput: PendingChatInput;
}>('SET_PENDING_CHAT_INPUT');
export const setChatReferenceText = createAction<string | null>('SET_CHAT_REFERENCE_TEXT');
export const setAuthUser = createAction<AuthUser>('SET_AUTH_USER');
export const setToolModalMousePosition = createAction<Point | null>(
	'SET_TOOL_MODAL_MOUSE_POSITION'
);
export const setFigmaIntegrationEnabled = createAction<boolean>('SET_FIGMA_INTEGRATION_ENABLED');
export const updateTransformDuringAnimation = createAction<{
	sceneId: string;
	transform: SceneTransform;
}>('UPDATE_TRANSFORM_DURING_ANIMATION');
export const cancelTransformAnimation = createAction('CANCEL_TRANSFORM_ANIMATION');
export const setVisibleToolModal = createAction<ToolType | null>('SET_VISIBLE_TOOL_MODAL');
export const setZoomPreviewBox = createAction<Rect | null>('SET_ZOOM_PREVIEW_BOX');
export const setSettings = createAction<Partial<Settings>>('SET_SETTINGS');
export const setChatQuality = createAction<Quality>('SET_CHAT_QUALITY');
export const setMergeInstruction = createAction<{ frameId: string; instruction: string }>(
	'SET_MERGE_INSTRUCTION'
);
export const setActiveLeftPanelTab = createAction<LeftPanelTab>('SET_ACTIVE_LEFT_PANEL_TAB');
export const setFrameIdToScrollTo = createAction<string | null>('mode/setFrameIdToScrollTo');
export const setCenterFramesAsChatGeneratesThem = createAction<boolean>(
	'mode/setCenterFramesAsChatGeneratesThem'
);
export const setPrototypeScaling = createAction<'mockup' | 'actual-size'>('SET_PROTOTYPE_SCALING');
export const doCommand = createAction<Command>('DO_COMMAND');
export const undoCommand = createAction('UNDO_COMMAND');
export const redoCommand = createAction('REDO_COMMAND');
export const setFrameCentered = createAction<string | null>('SET_FRAME_CENTERED');
export const setCenteredFrameId = createAction<string | null>('SET_CENTERED_FRAME_ID');
export const setIsDragging = createAction<boolean>('SET_IS_DRAGGING');

export const setLearningCenterState = createAction<Partial<LearningCenterState>>(
	'SET_LEARNING_CENTER_STATE'
);
export const setGoldTooltipState = createAction<GoldTooltipState>('SET_GOLD_TOOLTIP_STATE');
export const setSilverTooltipState = createAction<SilverTooltipState>('SET_SILVER_TOOLTIP_STATE');
export const addTrackedUserId = createAction<string>('ADD_TRACKED_USER_ID');
export const removeTrackedUserId = createAction<string>('REMOVE_TRACKED_USER_ID');
export const setTrackedUserIds = createAction<string[]>('SET_TRACKED_USER_IDS');

export const setDeviceConfig = createAction<DeviceConfig>('SET_DEVICE_CONFIG');
